import { constants } from '@config';
import { isSafari } from 'src/utils/safari';

import TableCell from './table-cell';
import TableCornerGrip from './table-corner';
import TableEditor from './table-editor';
import TableQuery from './table-query';
import TableResize from './table-resize';
import TableRow from './table-row';
import TableSelection from './table-selection';

export const defaultFontSize = constants.table.defaultFontSize;
export const defaultBorderSettings = {
  where: 'lrtb',
  stroke: 'black',
  strokeWidth: 2,
  strokeDash: null
};

export const borderFlagsAll = ['t', 'r', 'b', 'l'];
export const borderFlagsHorizontal = 'tb';
export const borderFlagsVertical = 'lr';
export const borderFlagsTop = 't';
export const borderFlagsBottom = 'b';
export const borderFlagsRight = 'r';
export const borderFlagsLeft = 'l';

export const directionHorizontal = 'h';
export const directionVertical = 'v';

export const cellSizeDefault = { width: 100, height: 50 };
export const cellSizeMin = { width: 10, height: 10 };

export default class Table {
  constructor () {
    this.defaultFontSize = defaultFontSize;
    this.defaultBorderSettings = defaultBorderSettings;
    this.borderFlagsAll = borderFlagsAll;
    this.borderFlagsHorizontal = borderFlagsHorizontal;
    this.borderFlagsVertical = borderFlagsVertical;
    this.borderFlagsTop = borderFlagsTop;
    this.borderFlagsBottom = borderFlagsBottom;
    this.borderFlagsRight = borderFlagsRight;
    this.borderFlagsLeft = borderFlagsLeft;
    this.directionHorizontal = directionHorizontal;
    this.directionVertical = directionVertical;
    this.cellSizeDefault = cellSizeDefault;
    this.cellSizeMin = cellSizeMin;
    this.callbackUpdateTableToolBar = this.updateTableToolBar.bind(this);
    this.callbackCellHandler = this.callbackCellClick.bind(this);
    this.tableMutationObserver = null;
    INKIVE.History.on('update', this.onHistoryChange.bind(this));
  }

  shouldRenderTableGrips () {
    const isBrowserAllowed = !isSafari();
    const isApp = INKIVE.C.IS_MOBILE_APP;

    return isBrowserAllowed && !isApp;
  }

  buildCornerGrip (tableElement, event) {
    if (this.shouldRenderTableGrips()) {
      this.tableCornerGrip = this.tableCornerGrip ?? new TableCornerGrip();
      this.tableCornerGrip.buildGrips(tableElement);
    }

    svgedit.select.getSelectorManager().requestSelector(tableElement).resize();

    if (!this.tableSelectionInstance) {
      this.beginTableEditingMode(tableElement, event);
    }
  }

  removeCornerGrip (options) {
    this.tableCornerGrip?.removeGrips(options);
  }

  registerTableSelection (tableElement) {
    this.tableSelectionInstance = new TableSelection(tableElement);
  }

  unregisterTableSelection () {
    this.tableSelectionInstance = null;
  }

  beginTableEditingMode (tableElement, event) {
    const canvas = INKIVE.EditorFacade.canvas;
    const isTableGripClicked = !!event?.target?.closest('.table-grip');

    if (canvas.getMode() === 'select') {
      canvas.setMode('table-edit');
    }

    document.querySelector('#svgcontent g.main').style.pointerEvents = null;

    this.tableResizeInstance = new TableResize(tableElement, this);

    const isResizingAllowed = INKIVE.RestrictionsFacade.isActionAllowed('table_resize', tableElement);
    if (isResizingAllowed) {
      this.tableResizeInstance.enableResizing();

      TableQuery.getAllBorderElements(tableElement, false).forEach(borderElement => {
        borderElement.classList.add('table-border-editor');
        this.tableResizeInstance.registerBorder(borderElement);
      });
    }

    this.tableSelectionInstance = new TableSelection(tableElement);

    TableQuery.getAllCellElements(tableElement).forEach(cellElement => {
      this.registerCell(cellElement);
    });

    if (event && !isTableGripClicked) {
      this.callbackCellClick(event);
      INKIVE.EditorFacade.on('table.selection_changed', this.callbackUpdateTableToolBar);
    } else {
      INKIVE.EditorFacade.on('table.selection_changed', this.callbackUpdateTableToolBar);
      INKIVE.Menus.elementPopupMenu.refresh();
    }

    this.updateTableToolBar(tableElement);
    svgedit.select.getSelectorManager().requestSelector(tableElement).showGrips(false);
    svgedit.select.getSelectorManager().requestSelector(tableElement.closest('.table'))?.showGrips(true);
    this.createTableMutationObserver(tableElement);
  }

  endTableEditingMode (clearSelection = true) {
    const canvas = INKIVE.EditorFacade.editor.canvas;
    if (canvas.getMode() === 'table-edit') {
      canvas.setMode('select');
      clearSelection && canvas.clearSelection();
    }

    document.querySelector('#svgcontent g.main').style.pointerEvents = 'all';

    if (this.tableResizeInstance) {
      this.tableResizeInstance.disableResizing();
    }

    const tableElements = this.tableSelectionInstance
      ? [this.tableSelectionInstance.tableElement]
      : document.querySelectorAll('g.table');

    tableElements.forEach(tableElement => {
      TableQuery.getAllBorderElements(tableElement, true).forEach(borderElement => {
        borderElement.classList.remove('table-border-editor');
        this.tableResizeInstance && this.tableResizeInstance.unregisterBorder(borderElement);
      });

      this.tableResizeInstance = null;

      TableQuery.getAllCellElements(tableElement).forEach(cellElement => {
        this.unregisterCell(cellElement);
        const cellRect = cellElement.querySelector('rect');
        cellRect.style.removeProperty('fill');
      });
    });

    INKIVE.EditorFacade.off('table.selection_changed', this.callbackUpdateTableToolBar);

    this.tableSelectionInstance?.clear();
    this.tableSelectionInstance = null;
    INKIVE.Menus.elementPopupMenu.refresh();
    this.tableMutationObserver?.disconnect();
    this.tableMutationObserver = null;
  }

  registerCell (cellElement) {
    cellElement.querySelector('g.textarea')?.addEventListener('mousedown', this.callbackCellHandler);
  }

  // Removes events from the given cell so it cannot be selected by mouse
  // Used to quit table edit mode
  unregisterCell (cellElement) {
    cellElement.querySelector('g.textarea')?.removeEventListener('mousedown', this.callbackCellHandler);
  }

  // Clear current selection
  clear () {
    const cellRectElements = this.tableSelectionInstance.tableElement.querySelectorAll('rect.table-cell-selected');

    cellRectElements.forEach(cellRectElement => {
      cellRectElement.classList.remove('table-cell-selected');
    });

    this.tableSelectionInstance.numberOfSelectedCells = 0;
  }

  callbackCellClick (event) {
    if (INKIVE.EditorFacade.editor.canvas.getMode() === 'textedit') return;

    event.shiftKey || this.clear();

    // Mouse can interact with cell's text or its rect
    let target = event.target;

    if (!target) return;

    const parentDistance = {
      rect: 0,
      text: 2,
      tspan: 3
    }[target.tagName];

    // We need just the rect element
    for (let i = 0; i < parentDistance; i++) {
      target = target.parentElement;
    }

    if (parentDistance) target = target.querySelector('rect');

    if (target.tagName !== 'rect') return;

    if (target.classList.contains('table-cell-selected') && event.shiftKey) {
      target.classList.remove('table-cell-selected');
      this.tableSelectionInstance.numberOfSelectedCells -= 1;
    } else if (INKIVE.EditorFacade.editor.canvas.getMode() === 'table-edit') {
      target.classList.add('table-cell-selected');
      this.tableSelectionInstance.numberOfSelectedCells += 1;
    }

    this.tableSelectionInstance.toggleTableSelectorBorder();
    INKIVE.Menus.elementPopupMenu.refresh();
    INKIVE.EditorFacade.trigger('table.selection_changed');
  }

  renderAllTexts () {
    if (!this.tableSelectionInstance) return;

    const textareas = this.tableSelectionInstance.tableElement.querySelectorAll('g.textarea');

    textareas.forEach(textarea => {
      const textArea = new INKIVE.Text.Textarea().setDOM(textarea);
      INKIVE.EditorFacade.editor.canvas.runExtensions('renderTextArea', { textArea });
    });
  }

  renderMergedCellTexts (mergeIds) {
    if (!this.tableSelectionInstance) return;

    const textareas = mergeIds.map((id) => {
      const masterCell = TableQuery.getMergeMaster(this.tableSelectionInstance.tableElement, id);

      return masterCell?.querySelector('g.textarea');
    });

    textareas.forEach(textarea => {
      const textArea = new INKIVE.Text.Textarea().setDOM(textarea);
      INKIVE.EditorFacade.editor.canvas.runExtensions('renderTextArea', { textArea });
    });
  }

  renderSpecificCellText (rowIndex, columnIndex) {
    if (!this.tableSelectionInstance) return;

    const cell = TableQuery.getCellElementAt(this.tableSelectionInstance.tableElement, rowIndex, columnIndex);

    if (!cell) return;
    const textArea = new INKIVE.Text.Textarea().setDOM(cell?.querySelector('g.textarea'));
    INKIVE.EditorFacade.editor.canvas.runExtensions('renderTextArea', { textArea });
  }

  /**
   *
   * @param {HTMLElement} cell
   * @return {number} innerCellHeight
   */
  getInnerCellHeight (cell) {
    const masterCell = TableQuery.getMasterCell(cell);
    const rightBottomCell = TableQuery.getRightBottomCell(cell);
    const topBorderWidth = Number(TableQuery.getBorderElementByCell(masterCell, borderFlagsTop)?.getAttribute('stroke-width')) || 0;
    const bottomBorderWidth = Number(TableQuery.getBorderElementByCell(rightBottomCell, borderFlagsBottom)?.getAttribute('stroke-width')) || 0;
    const padding = TableQuery.getCellPadding(cell);
    const cellRectElement = cell.querySelector('rect:not(.highlight-rect)');
    return Number(cellRectElement.getAttribute('height')) - topBorderWidth - bottomBorderWidth - padding.top - padding.bottom;
  }

  getTextVerticalOffset (textAreaElement, textHeight) {
    if (!textAreaElement.closest('.element.table')) {
      return 0;
    }
    const cell = textAreaElement.parentElement;

    const isCenterAligned = textAreaElement.classList.contains('vertical-center');
    const isBottomAligned = textAreaElement.classList.contains('vertical-bottom');
    const padding = TableQuery.getCellPadding(textAreaElement.parentElement);

    if (isCenterAligned || isBottomAligned) {
      const innerCellHeight = this.getInnerCellHeight(cell);
      const factor = isCenterAligned ? 0.5 : 1;
      const verticalShift = isCenterAligned ? 0 : padding.bottom;
      return Math.max((innerCellHeight - textHeight) * factor - verticalShift, padding.top);
    }

    return padding.top;
  }

  haveMergedCells (cellElements) {
    for (let i = 0; i < cellElements.length; i++) {
      if (TableQuery.isMerged(cellElements[i])) return true;
    }

    return false;
  }

  rowHasMergedCells (rowIndex) {
    if (!this.tableSelectionInstance) return undefined;

    const cellElements = TableQuery.getAllCellElementsAtRow(this.tableSelectionInstance.tableElement, rowIndex);
    return this.haveMergedCells(cellElements);
  }

  columnHasMergedCells (columnIndex) {
    if (!this.tableSelectionInstance) return undefined;

    const cellElements = TableQuery.getAllCellElementsAtColumn(this.tableSelectionInstance.tableElement, columnIndex);
    return this.haveMergedCells(cellElements);
  }

  getMergedData (classList) {
    const newClassList = [];

    classList.forEach(className => {
      if (className.startsWith('table-cell-max')) {
        newClassList.push('table-cell-min');
      } else if (className.startsWith('table-cell-min')) {
        newClassList.push('table-cell-min');
      } else if (className.startsWith('m-')) {
        newClassList.push(className);
      }
    });

    return newClassList.join(' ');
  }

  insertRow (beforeSelectedCell, rowIndex) {
    if (!this.tableSelectionInstance) return;

    if (!this.tableResizeInstance) this.tableResizeInstance = new TableResize(this.tableSelectionInstance.tableElement, this);

    const cellTarget = typeof rowIndex !== 'undefined'
      ? TableQuery.getCellElementAt(this.tableSelectionInstance.tableElement, rowIndex, 0)
      : this.tableSelectionInstance.getSelectedCellElements()?.[0];
    if (!cellTarget) return;

    const tableElement = this.tableSelectionInstance.tableElement;
    const tableEditor = new TableEditor(tableElement);

    const cellData = TableQuery.getCellData(cellTarget, true, true, true, true);
    const selectedIndex = cellData.rowIndex;

    const rowCellElements = TableQuery.getAllCellElementsAtRow(tableElement, selectedIndex);

    const rowCellData = [];
    const rowCellTextData = [];
    const rowCellBorderData = [];
    const rowCellMergeData = [];

    for (let i = 0; i < rowCellElements.length; i++) {
      const isMaster = TableQuery.isMergeMaster(rowCellElements[i]);
      const cellData = TableQuery.getCellData(rowCellElements[i], true, true, true, true);

      if (isMaster) {
        const { width, height } = TableQuery.getMasterCellDimension(tableElement, rowCellElements[i]);
        cellData.width = width ?? cellData.width;
        cellData.height = height ?? cellData.height;
      }

      rowCellData.push(cellData);
      rowCellTextData.push(TableQuery.getCellTextData(rowCellElements[i], true, false, true));

      const borderData = {};
      borderFlagsAll.forEach(which => {
        const cellBorderElement = TableQuery.getBorderElementByCell(rowCellElements[i], which);
        borderData[which] = TableQuery.getBorderData(cellBorderElement, false, false, true);
      });

      rowCellBorderData.push(borderData);
      rowCellMergeData.push(TableQuery.getMergeId(rowCellElements[i]));
    }

    let selectedHeight = Infinity;
    let selectedY = 0;

    for (const rowCellDatum of rowCellData) {
      const cellHeight = rowCellDatum.height;
      const cellY = rowCellDatum.y;
      if (cellHeight < selectedHeight) {
        selectedHeight = cellHeight;
        selectedY = cellY;
      }
    }

    const newRowIndex = beforeSelectedCell ? selectedIndex : selectedIndex + 1;
    const row = tableEditor.insertRow(newRowIndex, selectedHeight);

    for (let j = 0; j < rowCellData.length; j++) {
      const cellData = rowCellData[j];
      const mergedData = this.getMergedData(cellData.classList);

      const newY = beforeSelectedCell ? selectedY : selectedY + selectedHeight;
      const newCell = row.insertCell(cellData.x, newY, cellData.width, selectedHeight, j, mergedData);
      const newCellElement = newCell.cellElement;

      this.registerCell(newCellElement);

      newCellElement.querySelector('rect').setAttribute('fill', cellData.fill);

      const rowCellTextDatum = rowCellTextData[j];

      const newCellTextElement = newCellElement.querySelector('g.text');
      newCellTextElement.setAttribute('font-family', rowCellTextDatum.fontFamily);
      newCellTextElement.setAttribute('font-size', rowCellTextDatum.fontSize);
      newCellTextElement.setAttribute('font-weight', rowCellTextDatum.fontWeight);
      newCellTextElement.setAttribute('font-style', rowCellTextDatum.fontStyle);
      newCellTextElement.setAttribute('fill', rowCellTextDatum.fill);

      newCell.setTextAlignment(rowCellTextDatum.textAnchor, rowCellTextDatum.verticalAlignment);

      const rowCellBorderDatum = rowCellBorderData[j];
      borderFlagsAll.forEach(which => {
        const borderSettings = {
          where: which,
          stroke: rowCellBorderDatum[which].stroke,
          strokeWidth: rowCellBorderDatum[which].strokeWidth,
          strokeDash: rowCellBorderDatum[which].strokeDash
        };
        const borders = tableEditor.addBorder(newCellElement, borderSettings);
        borders.forEach(border => this.tableResizeInstance.registerBorder(border.borderElement));
      });
    }

    const uniqueMergedCell = [...new Set(rowCellMergeData)];
    for (const id of uniqueMergedCell) {
      if (id === -1 || !id) continue;

      this.updateMergedCell(id, tableEditor);
    }

    this.updateTableRect(null, true);

    INKIVE.DesignFacade.trigger('table.update');
  }

  deleteRow () {
    if (!this.tableSelectionInstance) return;

    const selectedCellElements = this.tableSelectionInstance.getSelectedCellElements();
    if (!selectedCellElements.length) return;

    const tableElement = this.tableSelectionInstance.tableElement;
    const cellData = TableQuery.getCellData(selectedCellElements[0], true, true, true, true);
    const selectedIndex = cellData.rowIndex;
    const rowCellElements = TableQuery.getAllCellElementsAtRow(tableElement, selectedIndex);
    const rowCellMergeData = [...rowCellElements].map(element => TableQuery.getMergeId(element));
    const tableEditor = new TableEditor(tableElement);

    const uniqueMergedCell = [...new Set(rowCellMergeData)].filter(id => id && id !== -1);
    const ranges = [];
    let textCellToRender = [];

    for (const id of uniqueMergedCell) {
      if (id === -1 || !id) continue;

      const mergedCells = tableElement.querySelectorAll(`.table-cell.m-${id}`);
      const mergeRange = this.getMergeRange(mergedCells);
      mergedCells.forEach(selectedCellElement => {
        tableEditor.split(selectedCellElement);
      });

      mergeRange.row[1] -= 1;
      if (mergeRange.column[0] === mergeRange.column[1] && mergeRange.row[0] === mergeRange.row[1]) {
        textCellToRender = [mergeRange.row[0], mergeRange.column[0]];
        continue;
      }

      // Do not add invalid range
      if (mergeRange.row[1] < mergeRange.row[0]) continue;

      ranges.push(mergeRange);
    }

    tableEditor.deleteRow(cellData.rowIndex);

    const mergeIds = [];
    for (const range of ranges) {
      mergeIds.push(tableEditor.merge(range));
    }

    this.updateTableRect(null, false);
    this.renderMergedCellTexts([...new Set(mergeIds)]);

    if (textCellToRender.length) {
      this.renderSpecificCellText(textCellToRender[0], textCellToRender[1]);
    }

    this.endTableEditingMode();

    INKIVE.DesignFacade.trigger('table.update');
  }

  insertColumn (beforeSelectedCell, columnIndex) {
    if (!this.tableSelectionInstance) return;

    if (!this.tableResizeInstance) this.tableResizeInstance = new TableResize(this.tableSelectionInstance.tableElement, this);

    const cellTarget = typeof columnIndex !== 'undefined'
      ? TableQuery.getCellElementAt(this.tableSelectionInstance.tableElement, 0, columnIndex)
      : this.tableSelectionInstance.getSelectedCellElements()?.[0];
    if (!cellTarget) return;

    const tableElement = this.tableSelectionInstance.tableElement;
    const tableEditor = new TableEditor(tableElement);

    const cellData = TableQuery.getCellData(cellTarget, true, true, true, true);
    const selectedIndex = cellData.columnIndex;

    const columnCellElements = TableQuery.getAllCellElementsAtColumn(tableElement, selectedIndex);
    const columnCellData = [];
    const columnCellTextData = [];
    const columnCellBorderData = [];
    const columnCellMergeData = [];

    for (let i = 0; i < columnCellElements.length; i++) {
      const isMaster = TableQuery.isMergeMaster(columnCellElements[i]);
      const cellData = TableQuery.getCellData(columnCellElements[i], true, true, true, true);

      if (isMaster) {
        const { width, height } = TableQuery.getMasterCellDimension(tableElement, columnCellElements[i]);
        cellData.width = width ?? cellData.width;
        cellData.height = height ?? cellData.height;
      }

      columnCellData.push(cellData);
      columnCellTextData.push(TableQuery.getCellTextData(columnCellElements[i], true, false, true));

      const borderData = {};
      borderFlagsAll.forEach(which => {
        const cellBorderElement = TableQuery.getBorderElementByCell(columnCellElements[i], which);
        borderData[which] = TableQuery.getBorderData(cellBorderElement, false, false, true);
      });

      columnCellBorderData.push(borderData);
      columnCellMergeData.push(TableQuery.getMergeId(columnCellElements[i]));
    }

    let selectedWidth = Infinity;
    let selectedX = 0;

    for (const columnCellDatum of columnCellData) {
      const cellWidth = columnCellDatum.width;
      const cellX = columnCellDatum.x;
      if (cellWidth < selectedWidth) {
        selectedWidth = cellWidth;
        selectedX = cellX;
      }
    }

    const newColumnIndex = beforeSelectedCell ? selectedIndex : selectedIndex + 1;
    tableEditor.insertColumn(newColumnIndex, selectedWidth);

    for (let j = 0; j < columnCellData.length; j++) {
      const cellData = columnCellData[j];
      const mergedData = this.getMergedData(cellData.classList);

      const rowElement = TableQuery.getRowElementAt(tableElement, cellData.rowIndex);
      const row = new TableRow();
      row.fromElement(rowElement);

      const newX = beforeSelectedCell ? selectedX : selectedX + selectedWidth;
      const newCell = row.insertCell(newX, cellData.y, selectedWidth, cellData.height, newColumnIndex, mergedData);
      const newCellElement = newCell.cellElement;

      this.registerCell(newCellElement);

      newCellElement.querySelector('rect').setAttribute('fill', cellData.fill);

      const columnCellTextDatum = columnCellTextData[j];

      const newCellTextElement = newCellElement.querySelector('g.text');
      newCellTextElement.setAttribute('font-family', columnCellTextDatum.fontFamily);
      newCellTextElement.setAttribute('font-size', columnCellTextDatum.fontSize);
      newCellTextElement.setAttribute('font-weight', columnCellTextDatum.fontWeight);
      newCellTextElement.setAttribute('font-style', columnCellTextDatum.fontStyle);
      newCellTextElement.setAttribute('fill', columnCellTextDatum.fill);

      newCell.setTextAlignment(columnCellTextDatum.textAnchor, columnCellTextDatum.verticalAlignment);

      const columnCellBorderDatum = columnCellBorderData[j];
      borderFlagsAll.forEach(which => {
        const borderSettings = {
          where: which,
          stroke: columnCellBorderDatum[which].stroke,
          strokeWidth: columnCellBorderDatum[which].strokeWidth,
          strokeDash: columnCellBorderDatum[which].strokeDash
        };
        const borders = tableEditor.addBorder(newCellElement, borderSettings);
        borders.forEach(border => this.tableResizeInstance.registerBorder(border.borderElement));
      });
    }

    const uniqueMergedCell = [...new Set(columnCellMergeData)];
    for (const id of uniqueMergedCell) {
      if (id === -1 || !id) continue;

      this.updateMergedCell(id, tableEditor);
    }

    const { newX, newY, oldWidth, newWidth } = this.updateTableRect(null, false);
    const transform = `translate(${newX}, ${newY}) scale(${oldWidth / newWidth}, 1) translate(${-newX}, ${-newY})`;
    tableElement.setAttribute('transform', transform);

    // TODO: there is performance issue with recalculateDimensions since it re-renders all the texts and triggers redundant stretchRow calls
    // The issue also happens when moving or scale the table (apply transform to all children elements)
    svgedit.recalculate.recalculateDimensions(tableElement);

    // Re-update the rect since recalculateDimensions does text resizing
    this.updateTableRect(null, false);

    INKIVE.DesignFacade.trigger('table.update');
  }

  updateMergedCell (mergeId, tableEditor) {
    const mergedCells = this.tableSelectionInstance.tableElement.querySelectorAll(`.table-cell.m-${mergeId}`);
    const mergeRange = this.getMergeRange(mergedCells);

    mergedCells.forEach(selectedCellElement => {
      tableEditor.split(selectedCellElement);
    });

    tableEditor.merge(mergeRange);
  }

  deleteColumn () {
    if (!this.tableSelectionInstance) return;

    const selectedCellElements = this.tableSelectionInstance.getSelectedCellElements();
    if (!selectedCellElements.length) return;

    const tableElement = this.tableSelectionInstance.tableElement;
    const cellData = TableQuery.getCellData(selectedCellElements[0], true, false, false, false);
    const selectedIndex = cellData.columnIndex;
    const columnCellElements = TableQuery.getAllCellElementsAtColumn(tableElement, selectedIndex);

    const columnCellMergeData = [...columnCellElements].map(element => TableQuery.getMergeId(element));
    const tableEditor = new TableEditor(tableElement);
    const oldWidth = Number(tableElement.querySelector('rect').getAttribute('width'));

    const uniqueMergedCell = [...new Set(columnCellMergeData)];
    const ranges = [];
    for (const id of uniqueMergedCell) {
      if (id === -1 || !id) continue;

      const mergedCells = tableElement.querySelectorAll(`.table-cell.m-${id}`);
      const mergeRange = this.getMergeRange(mergedCells);
      mergedCells.forEach(selectedCellElement => {
        tableEditor.split(selectedCellElement);
      });

      mergeRange.column[1] -= 1;
      if (mergeRange.column[0] === mergeRange.column[1] && mergeRange.row[0] === mergeRange.row[1]) continue;

      // Do not add invalid range
      if (mergeRange.column[1] < mergeRange.column[0]) continue;

      ranges.push(mergeRange);
    }

    tableEditor.deleteColumn(cellData.columnIndex);

    for (const range of ranges) {
      tableEditor.merge(range);
    }

    const { newX, newY, newWidth } = this.updateTableRect(null, false);
    const transform = `translate(${newX}, ${newY}) scale(${oldWidth / newWidth}, 1) translate(${-newX}, ${-newY})`;
    tableElement.setAttribute('transform', transform);

    // Different than deleteRow, the below function will re-render text
    svgedit.recalculate.recalculateDimensions(tableElement);
    this.updateTableRect(null, false);

    INKIVE.DesignFacade.trigger('table.update');
  }

  setBorderStyle (borderSettings) {
    if (!this.tableSelectionInstance) return;

    let selectedCellElements = this.tableSelectionInstance.getSelectedCellElements();
    if (!selectedCellElements.length) {
      selectedCellElements = this.tableSelectionInstance.getAllCellElements();
    }

    const tableElement = this.tableSelectionInstance.tableElement;
    const tableEditor = new TableEditor(tableElement);

    selectedCellElements.forEach(selectedCellElement => {
      if (TableQuery.isMerged(selectedCellElement)) {
        const mergeId = TableQuery.getMergeId(selectedCellElement);
        const nearbyMergedCells = tableElement.querySelectorAll(`.table-cell.m-${mergeId}`);
        const mergeRange = this.getMergeRange(nearbyMergedCells);

        for (let r = mergeRange.row[0]; r <= mergeRange.row[1]; r++) {
          for (let c = mergeRange.column[0]; c <= mergeRange.column[1]; c++) {
            // Set exterior borders
            const borderNames = [];
            r === mergeRange.row[0] && borderSettings.where.indexOf(borderFlagsTop) !== -1 && borderNames.push(borderFlagsTop);
            r === mergeRange.row[1] && borderSettings.where.indexOf(borderFlagsBottom) !== -1 && borderNames.push(borderFlagsBottom);
            c === mergeRange.column[0] && borderSettings.where.indexOf(borderFlagsLeft) !== -1 && borderNames.push(borderFlagsLeft);
            c === mergeRange.column[1] && borderSettings.where.indexOf(borderFlagsRight) !== -1 && borderNames.push(borderFlagsRight);

            const where = borderNames.join('');
            tableEditor.setBorderStyle(r, c, { ...borderSettings, where });
          }
        }
      } else {
        const cellData = TableQuery.getCellData(selectedCellElement, true, false, false, false);
        tableEditor.setBorderStyle(cellData.rowIndex, cellData.columnIndex, borderSettings);
      }
    });

    INKIVE.DesignFacade.trigger('table.update');
  }

  setTextAlignment (horizontal, vertical) {
    if (!this.tableSelectionInstance) return;

    let selectedCellElements = this.tableSelectionInstance.getSelectedCellElements();

    if (selectedCellElements.length === 0) {
      selectedCellElements = this.tableSelectionInstance.getAllCellElements();
    }

    selectedCellElements.forEach(selectedCellElement => {
      const selectedCell = new TableCell();
      selectedCell.fromElement(selectedCellElement);
      selectedCell.setTextAlignment(horizontal, vertical);
    });

    INKIVE.DesignFacade.trigger('table.update');
  }

  getMergeRange (selectedCellElements) {
    if (selectedCellElements.length < 2) return null;

    const rowIndices = [];
    const columnIndices = [];

    selectedCellElements.forEach(selectedCellElement => {
      const cellData = TableQuery.getCellData(selectedCellElement, true, false, false, false);
      rowIndices.push(cellData.rowIndex);
      columnIndices.push(cellData.columnIndex);
    });

    rowIndices.sort();
    columnIndices.sort();

    return {
      row: [rowIndices[0], rowIndices[rowIndices.length - 1]],
      column: [columnIndices[0], columnIndices[columnIndices.length - 1]]
    };
  }

  canMerge () {
    if (!this.tableSelectionInstance) return false;

    const mergeRange = this.getMergeRange(this.tableSelectionInstance.getSelectedCellElements());
    if (!mergeRange) return false;

    const tableElement = this.tableSelectionInstance.tableElement;

    for (let r = mergeRange.row[0]; r <= mergeRange.row[1]; r++) {
      for (let c = mergeRange.column[0]; c <= mergeRange.column[1]; c++) {
        const cellElement = TableQuery.getCellElementAt(tableElement, r, c);
        // Cell element should not be part of a merge operation
        if (TableQuery.isMerged(cellElement)) return false;

        const rectElement = cellElement.querySelector('rect');
        if (!rectElement.classList.contains('table-cell-selected')) return false;
      }
    }

    return true;
  }

  merge (mergeRange) {
    if (!this.tableSelectionInstance) return;

    mergeRange = mergeRange ?? this.getMergeRange(this.tableSelectionInstance.getSelectedCellElements());
    if (!mergeRange) return;

    const tableEditor = new TableEditor(this.tableSelectionInstance.tableElement);
    tableEditor.merge(mergeRange);

    this.tableSelectionInstance.clear();
    this.renderAllTexts();

    INKIVE.DesignFacade.trigger('table.update');
  }

  canSplit () {
    if (!this.tableSelectionInstance) return false;

    const selectedCellElements = this.tableSelectionInstance.getSelectedCellElements();
    if (!selectedCellElements.length) return false;

    return [...selectedCellElements].every(selectedCellElement => {
      return TableQuery.isMergeMaster(selectedCellElement);
    });
  }

  split () {
    if (!this.tableSelectionInstance) return;

    const tableEditor = new TableEditor(this.tableSelectionInstance.tableElement);

    const selectedCellElements = this.tableSelectionInstance.getSelectedCellElements();
    selectedCellElements.forEach(selectedCellElement => {
      tableEditor.split(selectedCellElement);
    });

    this.tableSelectionInstance.clear();
    this.renderAllTexts();

    INKIVE.DesignFacade.trigger('table.update');
  }

  /**
   *
   * @param {HTMLElement} tableElement
   * @param {HTMLElement} cellElement
   * @param {number} delta
   * @param {boolean} forceUpdateSelector
   * @param {boolean} allowResizeBelowMinValue
   */
  stretchRow (tableElement, cellElement, delta, forceUpdateSelector = true, allowResizeBelowMinValue = true) {
    if (!tableElement) return;

    const cellData = TableQuery.getCellData(cellElement, true, false, false, false);

    const tableEditor = new TableEditor(tableElement);
    tableEditor.stretchRow(cellData.rowIndex, delta, cellElement, allowResizeBelowMinValue);

    const tableResize = new TableResize(tableElement);
    tableResize.resizeMergedCells(cellData.rowIndex, delta);

    this.updateTableRect(tableElement, forceUpdateSelector);
  }

  convertBorderNameToFlag (borderName) {
    let where;

    switch (borderName) {
      case 'vertical':
        where = borderFlagsVertical;
        break;
      case 'horizontal':
        where = borderFlagsHorizontal;
        break;
      case 'left':
        where = borderFlagsLeft;
        break;
      case 'right':
        where = borderFlagsRight;
        break;
      case 'top':
        where = borderFlagsTop;
        break;
      case 'bottom':
        where = borderFlagsBottom;
        break;
    }

    return where;
  }

  setFontSize (newFontSize) {
    if (!this.tableSelectionInstance) return;

    const selectedCellElements = this.tableSelectionInstance.getSelectedCellElements();
    selectedCellElements.forEach(selectedCellElement => {
      const textAreaElement = selectedCellElement.querySelector('g.textarea');

      [...textAreaElement.classList].forEach(cls => {
        if (cls.match(/original-font-size-.*/)) {
          textAreaElement.classList.remove(cls);
          cls = `original-font-size-${newFontSize}`;
          textAreaElement.classList.add(cls);
        }

        selectedCellElement.querySelector('g.text').setAttribute('data-original-font-size', newFontSize);
      });
    });

    this.renderAllTexts();

    INKIVE.DesignFacade.trigger('table.update');
  }

  setFontFamily (newFontFamily) {
    if (!this.tableSelectionInstance) return;

    const selectedCellElements = this.tableSelectionInstance.getSelectedCellElements();
    selectedCellElements.forEach(selectedCellElement => {
      const textElement = selectedCellElement.querySelector('g.text');
      textElement.setAttribute('font-family', newFontFamily);
    });

    this.renderAllTexts();

    INKIVE.DesignFacade.trigger('table.update');
  }

  updateTableRect (tableElement, forceUpdateSelector = false) {
    if (!tableElement && this.tableSelectionInstance) {
      tableElement = this.tableSelectionInstance.tableElement;
    }
    if (!tableElement) return;

    const rowElements = tableElement.querySelectorAll('g.table-row');
    const lastRowIndex = rowElements.length - 1;

    const lastRowCellElements = TableQuery.getAllCellElementsAtRow(tableElement, lastRowIndex);
    const lastColumnIndex = lastRowCellElements.length - 1;

    const topLeftCell = TableQuery.getCellElementAt(tableElement, 0, 0);
    const bottomRightCell = TableQuery.getCellElementAt(tableElement, lastRowIndex, lastColumnIndex);

    const topLeftCellData = TableQuery.getCellData(topLeftCell, false, true, true, false, true);
    const bottomRightCellData = TableQuery.getCellData(bottomRightCell, false, true, true, false, true);

    const x0 = topLeftCellData.x;
    const y0 = topLeftCellData.y;

    const x1 = bottomRightCellData.x;
    const y1 = bottomRightCellData.y;
    const width = bottomRightCellData.width;
    const height = bottomRightCellData.height;

    const rectElement = tableElement.querySelector('rect');
    const oldRectWidth = Number(rectElement.getAttribute('width'));

    rectElement.setAttribute('width', x1 - x0 + width);
    rectElement.setAttribute('height', y1 - y0 + height);
    rectElement.setAttribute('x', x0);
    rectElement.setAttribute('y', y0);

    forceUpdateSelector && svgedit.select.getSelectorManager().requestSelector(tableElement).resize();

    return {
      newX: x0,
      newY: y0,
      oldWidth: oldRectWidth,
      newWidth: x1 - x0 + width
    };
  }

  updateTableToolBar (tableElement) {
    if (!this.tableSelectionInstance) {
      this.updateTableToolBarInSelectMode(tableElement);
      return;
    }

    let backgroundColor = '#000000';
    let foregroundColor = '#000000';
    let highlightColor = '#000000';
    let isBold;
    let isItalic;
    let isUnderlined;
    let isOrderedList;
    let isBulletsList;
    let isUpperCase;

    const selectedCellElements = this.tableSelectionInstance.getSelectedCellElements();
    selectedCellElements.forEach(selectedCellElement => {
      const cellData = TableQuery.getCellData(selectedCellElement, false, false, false, true);
      const cellTextData = TableQuery.getCellTextData(selectedCellElement, true, false, false);

      backgroundColor = cellData.fill;
      foregroundColor = cellTextData.fill;
      highlightColor = cellTextData.highlight;
      isOrderedList = cellTextData.isOrderedList;
      isBulletsList = cellTextData.isBulletsList;
      isUpperCase = cellTextData.isUpperCase;

      if (typeof isBold === 'undefined') {
        isBold = cellTextData.fontWeight === 700;
      } else {
        isBold = isBold && cellTextData.fontWeight === 700;
      }

      if (typeof isItalic === 'undefined') {
        isItalic = cellTextData.fontStyle === 'italic';
      } else {
        isItalic = isItalic && cellTextData.fontStyle === 'italic';
      }

      if (typeof isUnderlined === 'undefined') {
        isUnderlined = cellTextData.textDecoration === 'underline';
      } else {
        isUnderlined = isUnderlined && cellTextData.textDecoration === 'underline';
      }
    });

    this.setColor('background', backgroundColor, 'i');
    this.setColor('foreground', foregroundColor, 'svg');
    this.setColor('highlight', highlightColor, 'svg');

    this.setApplied('bold', isBold);
    this.setApplied('italic', isItalic);
    this.setApplied('underline', isUnderlined);
    this.setApplied('list', isOrderedList);
    this.setApplied('bullets', isBulletsList);
    this.setApplied('case', isUpperCase);
  }

  updateTableToolBarInSelectMode (tableElement) {
    if (!tableElement) return;

    const isBold = !tableElement.querySelector('[font-weight="400"], [font-weight="normal"]');
    const isItalic = !!tableElement.querySelector('[font-style="italic"]');
    const isUnderlined = !!tableElement.querySelector('[text-decoration="underline"]');
    const highlight = tableElement.querySelector('[highlight]')?.getAttribute('highlight') || '#000000';
    const isOrderedList = !!tableElement.querySelector('.ordered');
    const isBulletsList = !!tableElement.querySelector('.bullets');
    const isUpperCase = tableElement.textContent !== tableElement.textContent.toLowerCase();

    this.setColor('highlight', highlight, 'svg');

    this.setApplied('bold', isBold);
    this.setApplied('italic', isItalic);
    this.setApplied('underline', isUnderlined);
    this.setApplied('list', isOrderedList);
    this.setApplied('bullets', isBulletsList);
    this.setApplied('case', isUpperCase);
  }

  setApplied (keyword, applied) {
    const btn = document.querySelector(`a.btn[data-action="on_cell-${keyword}"]`);

    if (btn) {
      applied && (btn.classList.contains('applied') || btn.classList.add('applied'));
      applied || (btn.classList.contains('applied') && btn.classList.remove('applied'));
    }
  }

  setColor (type, fill, selector) {
    const button = document.querySelector(`a[type="cell_${type}"] ${selector}`);
    if (button) {
      if (selector === 'i') {
        if (fill.toLowerCase() === 'none') {
          button.style.color = '#00000000';
          button.style.backgroundImage = 'linear-gradient(45deg, #777 25%, transparent 25%, transparent 75%, #777 75%, #777), linear-gradient(45deg, #777 25%, transparent 25%, transparent 75%, #777 75%, #777)';
          button.style.backgroundSize = '10px 10px';
          button.style.backgroundPosition = '0 0, 5px 5px';
        } else {
          button.style.color = fill || '#000000';
          button.style.backgroundImage = 'inherit';
          button.style.backgroundSize = 'inherit';
          button.style.backgroundPosition = 'inherit';
        }
      } else {
        button.style.fill = fill || '#000000';
      }
    }
  }

  recalculateDimensions (tableElement) {
    const tlist = svgedit.transformlist.getTransformList(tableElement);
    if (tlist.numberOfItems === 0) return;

    const transform = getComputedStyle(tableElement).transform;
    if (!transform) return;

    const matrix = new DOMMatrixReadOnly(transform);

    recalculateDimensionsLoop(tableElement);

    tableElement.removeAttribute('transform');
    tlist.clear();

    function recalculateDimensionsLoop (element) {
      if (element.hasAttribute('x')) {
        pointFromMatrix(element, 'x', 'y');
      }

      if (element.hasAttribute('x1')) {
        pointFromMatrix(element, 'x1', 'y1');
        pointFromMatrix(element, 'x2', 'y2');
      }

      element.children.forEach(recalculateDimensionsLoop);
    }

    function pointFromMatrix (element, xName, yName) {
      const x = parseFloat(element.getAttribute(xName));
      const y = parseFloat(element.getAttribute(yName));
      const w = x * matrix.m14 + y * matrix.m24 + matrix.m44;

      const newX = x * matrix.m11 + y * matrix.m21 + matrix.m41;
      const newY = x * matrix.m12 + y * matrix.m22 + matrix.m42;

      element.setAttribute(xName, newX / w);
      element.setAttribute(yName, newY / w);
    }
  }

  clearTextInCells (cells) {
    cells.forEach(cell => {
      const textareaElement = cell.closest('g.textarea');
      const textArea = new INKIVE.Text.Textarea().setDOM(textareaElement);
      textArea.text.setValues({ textContent: '' });
      INKIVE.EditorFacade.editor.canvas.runExtensions('renderTextArea', { textArea, resize: false });
    });
  }

  /**
   *
   * @param {HTMLElement} selectedElement
   * rebuild table grip after history action such as undo and redo,
   * it will only rebuild the grip when there is only one selected element
   */
  onHistoryChange () {
    const selectedElements = INKIVE.EditorFacade.canvas.getSelectedElems();

    if (selectedElements.length > 1) return;

    const visibleElements = INKIVE.EditorFacade.canvas.getVisibleElements();
    const selectedTableElements = selectedElements.filter(elem => visibleElements.includes(elem) && elem.classList.contains('table'));
    selectedTableElements.forEach((elem) => this.buildCornerGrip(elem));
  }

  /**
   *
   * @param {HTMLElement} cell
   * @returns {number} masterRectHeight
   */
  getContentHeight (cell) {
    const rightBottomCell = TableQuery.getRightBottomCell(cell);
    const cellPadding = TableQuery.getCellPadding(rightBottomCell);

    if (cell.parentElement !== rightBottomCell.parentElement) {
      return 0;
    }

    const masterCell = TableQuery.getMasterCell(rightBottomCell);
    const masterRect = masterCell.querySelector('rect');
    const masterRectHeight = parseFloat(masterRect.getAttribute('height'));
    const masterText = masterCell.querySelector('.text');
    const masterTextBBox = masterText.getBBox();
    const topBorderWidth = Number(TableQuery.getBorderElementByCell(masterCell, borderFlagsTop)?.getAttribute('stroke-width') ?? '0');
    const bottomBorderWidth = Number(TableQuery.getBorderElementByCell(rightBottomCell, borderFlagsBottom)?.getAttribute('stroke-width') ?? '0');

    const sumOtherRowHeights = masterRectHeight - TableQuery.getCellData(rightBottomCell, false, false, true, false).height;

    return Math.max(masterTextBBox.height + cellPadding.top + cellPadding.bottom + topBorderWidth + bottomBorderWidth - sumOtherRowHeights, 0);
  }

  /**
   *
   * @param {HTMLElement} cell
   * @param {{allowResizeBelowMinValue: boolean}} options
   * @returns {number} deltaChange
   * resize last row in a merged cell
   * using master cell to calculate the delta height instead of the current cell
   */
  adaptRowHeightToContent (cell, options) {
    const allowResizeBelowMinValue = options?.allowResizeBelowMinValue ?? true;
    const rightBottomCell = TableQuery.getRightBottomCell(cell);

    const lastRow = rightBottomCell.parentElement;
    const cellsInRow = [...TableQuery.getAllCellElementsByRow(lastRow)];

    const masterCell = TableQuery.getMasterCell(rightBottomCell);
    const masterRect = masterCell.querySelector('rect');

    const masterInitialHeight = parseFloat(masterRect.getAttribute('height'));
    const masterText = masterCell.querySelector('.text');
    const masterTextBBox = masterText.getBBox();

    const maxOtherContentHeight = cellsInRow.reduce((acc, cell) => {
      if (cell !== rightBottomCell) {
        const otherContentHeight = this.getContentHeight(cell);
        return Math.max(otherContentHeight, acc);
      }
      return acc;
    }, 0);

    let minTableContentHeight = Number(lastRow.dataset.minContentHeight || '0');
    if ((minTableContentHeight === 0 || allowResizeBelowMinValue) && cell.parentElement === lastRow) {
      const currentCellHeight = parseFloat(cell.querySelector('rect')?.getAttribute('height') ?? '0');
      minTableContentHeight = Math.max(currentCellHeight, maxOtherContentHeight);
      lastRow.dataset.minContentHeight = minTableContentHeight.toString();
    }

    const minContentHeight = Math.max(minTableContentHeight, maxOtherContentHeight);

    const sumOtherRowHeights = masterInitialHeight - TableQuery.getCellData(rightBottomCell, false, false, true, false).height;

    const cellPadding = TableQuery.getCellPadding(rightBottomCell);
    const topBorderWidth = Number(TableQuery.getBorderElementByCell(masterCell, borderFlagsTop)?.getAttribute('stroke-width') ?? '0');
    const bottomBorderWidth = Number(TableQuery.getBorderElementByCell(rightBottomCell, borderFlagsBottom)?.getAttribute('stroke-width') ?? '0');
    const totalBorderWidth = (topBorderWidth + bottomBorderWidth);
    const masterRectHeight = Math.max(masterTextBBox.height + cellPadding.top + cellPadding.bottom + totalBorderWidth, minContentHeight + sumOtherRowHeights);
    const tableElement = rightBottomCell.parentElement.parentElement;

    const deltaChange = masterRectHeight - masterInitialHeight;
    if (deltaChange !== 0) {
      tableElement && this.stretchRow(tableElement, rightBottomCell, deltaChange, false, allowResizeBelowMinValue);
      INKIVE.DesignFacade.trigger('table.update');
    }

    return deltaChange;
  }

  createTableMutationObserver (tableElement) {
    if (this.tableMutationObserver) return;

    this.tableMutationObserver = new MutationObserver(this.handleAttributesMutation.bind(this));
    tableElement.querySelectorAll('g.table-cell rect').forEach(element => this.tableMutationObserver.observe(element, { attributes: true, attributeFilter: ['class', 'fill'] }));
  }

  handleAttributesMutation (mutationsList) {
    for (const mutation of mutationsList) {
      if (mutation.type !== 'attributes') return;

      const target = mutation.target;

      if (target.className.baseVal.includes('table-cell-selected')) {
        const currentFillColour = target.getAttribute('fill') || target.style.fill;
        const hasSecondaryColour = currentFillColour && currentFillColour.toLowerCase() !== 'none';
        if (hasSecondaryColour) {
          target.style.fill = `color-mix(in hsl, ${currentFillColour} 100%, rgba(65, 155, 249) 10%)`;
        }
      } else {
        target.style.removeProperty('fill');
      }
    }
  }
}
